import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Plus, CreditCard, Check, Loader2, ChevronRight, Truck, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageTransition } from "@/components/PageTransition";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MenuDrawer } from "@/components/MenuDrawer";

interface ShippingAddress {
  id: string;
  full_name: string;
  address_line: string;
  city: string;
  postal_code: string;
  province: string;
  phone: string | null;
  is_default: boolean;
}

const Checkout = () => {
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const [step, setStep] = useState(1); // 1=address, 2=review
  const [menuOpen, setMenuOpen] = useState(false);

  // Address state
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    full_name: "", address_line: "", city: "", postal_code: "", province: "", phone: "",
  });
  const [savingAddress, setSavingAddress] = useState(false);

  // Order state
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (items.length === 0) {
      navigate("/prodotti");
      return;
    }
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    const { data } = await supabase
      .from("shipping_addresses")
      .select("*")
      .order("is_default", { ascending: false });
    if (data) {
      setAddresses(data);
      const defaultAddr = data.find(a => a.is_default);
      if (defaultAddr) setSelectedAddressId(defaultAddr.id);
      else if (data.length > 0) setSelectedAddressId(data[0].id);
    }
  };

  const handleSaveAddress = async () => {
    if (!newAddress.full_name || !newAddress.address_line || !newAddress.city || !newAddress.postal_code || !newAddress.province) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSavingAddress(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non autenticato");

      const isFirst = addresses.length === 0;
      const { data, error } = await supabase
        .from("shipping_addresses")
        .insert({
          user_id: user.id,
          ...newAddress,
          is_default: isFirst,
        })
        .select()
        .single();

      if (error) throw error;
      setAddresses(prev => [...prev, data]);
      setSelectedAddressId(data.id);
      setShowNewAddress(false);
      setNewAddress({ full_name: "", address_line: "", city: "", postal_code: "", province: "", phone: "" });
      toast.success("Indirizzo salvato");
    } catch (error) {
      console.error(error);
      toast.error("Errore nel salvataggio dell'indirizzo");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedAddressId) {
      toast.error("Seleziona un indirizzo di spedizione");
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non autenticato");

      // Create order
      const { data: order, error: orderError } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          total_price: totalPrice,
          status: "pending",
          shipping_address_id: selectedAddressId,
          notes: notes || null,
        })
        .select()
        .single();

      if (orderError) throw orderError;

      // Create order items
      const orderItems = items.map(item => ({
        order_id: order.id,
        product_id: item.product.id,
        variant_id: item.variant.id,
        product_name: item.product.name,
        variant_info: `${item.selectedColor}${item.selectedMemory ? ` • ${item.selectedMemory}GB` : ''} • ${item.selectedCondition}`,
        cover_image: item.product.cover_image,
        quantity: item.quantity,
        unit_price: item.unitPrice,
      }));

      const { error: itemsError } = await supabase
        .from("order_items")
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // Send push notification to admin
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Get admin users
          const { data: adminRoles } = await supabase
            .from("user_roles")
            .select("user_id")
            .eq("role", "admin");
          
          if (adminRoles) {
            for (const role of adminRoles) {
              const { data: subs } = await supabase
                .from("push_subscriptions")
                .select("*")
                .eq("user_id", role.user_id);
              
              if (subs && subs.length > 0) {
                await supabase.functions.invoke("send-push-notification", {
                  headers: { Authorization: `Bearer ${session.access_token}` },
                  body: {
                    subscription: subs[0],
                    title: "🛒 Nuovo ordine!",
                    body: `Ordine di €${totalPrice.toFixed(2)} da confermare`,
                    url: "/profile",
                  },
                });
              }
            }
          }
        }
      } catch (e) {
        console.log("Push notification error (non-critical):", e);
      }

      clearCart();
      toast.success("Ordine inviato! Riceverai una notifica quando verrà confermato.");
      navigate("/profile");
    } catch (error) {
      console.error("Error submitting order:", error);
      toast.error("Errore nell'invio dell'ordine");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedAddress = addresses.find(a => a.id === selectedAddressId);

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-32">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-foreground">Checkout</h1>
              <p className="text-xs text-muted-foreground">Step {step} di 2</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-muted">
            <div className="h-full bg-primary transition-all duration-300" style={{ width: `${(step / 2) * 100}%` }} />
          </div>
        </header>

        <div className="container mx-auto px-4 py-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Address */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  Indirizzo di spedizione
                </h2>

                {/* Saved addresses */}
                {addresses.map(addr => (
                  <button
                    key={addr.id}
                    onClick={() => setSelectedAddressId(addr.id)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedAddressId === addr.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${selectedAddressId === addr.id ? "text-primary" : "text-muted-foreground"}`} />
                      <div>
                        <p className="font-semibold text-foreground">{addr.full_name}</p>
                        <p className="text-sm text-muted-foreground">{addr.address_line}</p>
                        <p className="text-sm text-muted-foreground">{addr.postal_code} {addr.city} ({addr.province})</p>
                        {addr.phone && <p className="text-sm text-muted-foreground">{addr.phone}</p>}
                      </div>
                      {selectedAddressId === addr.id && (
                        <Check className="w-5 h-5 text-primary ml-auto flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}

                {/* New address form */}
                {showNewAddress ? (
                  <div className="p-4 rounded-xl border border-border space-y-3">
                    <h3 className="font-semibold text-foreground">Nuovo indirizzo</h3>
                    <div>
                      <Label>Nome completo *</Label>
                      <Input value={newAddress.full_name} onChange={e => setNewAddress({ ...newAddress, full_name: e.target.value })} placeholder="Mario Rossi" />
                    </div>
                    <div>
                      <Label>Indirizzo *</Label>
                      <Input value={newAddress.address_line} onChange={e => setNewAddress({ ...newAddress, address_line: e.target.value })} placeholder="Via Roma 1" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Città *</Label>
                        <Input value={newAddress.city} onChange={e => setNewAddress({ ...newAddress, city: e.target.value })} placeholder="Milano" />
                      </div>
                      <div>
                        <Label>CAP *</Label>
                        <Input value={newAddress.postal_code} onChange={e => setNewAddress({ ...newAddress, postal_code: e.target.value })} placeholder="20100" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Provincia *</Label>
                        <Input value={newAddress.province} onChange={e => setNewAddress({ ...newAddress, province: e.target.value })} placeholder="MI" />
                      </div>
                      <div>
                        <Label>Telefono</Label>
                        <Input value={newAddress.phone} onChange={e => setNewAddress({ ...newAddress, phone: e.target.value })} placeholder="+39 333..." />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => setShowNewAddress(false)}>Annulla</Button>
                      <Button className="flex-1" onClick={handleSaveAddress} disabled={savingAddress}>
                        {savingAddress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Salva
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewAddress(true)}
                    className="w-full p-4 rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center gap-3 text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Aggiungi nuovo indirizzo</span>
                  </button>
                )}

                <Button
                  className="w-full h-12 text-base rounded-xl gap-2"
                  disabled={!selectedAddressId}
                  onClick={() => setStep(2)}
                >
                  Continua
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </motion.div>
            )}

            {/* Step 2: Review & Submit */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  Riepilogo ordine
                </h2>

                {/* Address summary */}
                {selectedAddress && (
                  <div className="p-4 rounded-xl bg-card border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">Spedizione a</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{selectedAddress.full_name}</p>
                    <p className="text-sm text-muted-foreground">{selectedAddress.address_line}, {selectedAddress.postal_code} {selectedAddress.city} ({selectedAddress.province})</p>
                  </div>
                )}

                {/* Items */}
                <div className="space-y-3">
                  {items.map(item => (
                    <div key={item.variant.id} className="flex gap-3 p-3 rounded-xl bg-card border border-border/50">
                      {item.product.cover_image && (
                        <img src={item.product.cover_image} alt={item.product.name} className="w-14 h-14 rounded-lg object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-foreground truncate">{item.product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.selectedColor}{item.selectedMemory ? ` • ${item.selectedMemory}GB` : ''} • {item.selectedCondition}
                        </p>
                        <p className="text-xs text-muted-foreground">Qtà: {item.quantity}</p>
                      </div>
                      <p className="font-bold text-sm">€{(item.unitPrice * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                {/* Notes */}
                <div>
                  <Label>Note (opzionale)</Label>
                  <Textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Eventuali note per l'ordine..."
                    className="resize-none"
                    rows={3}
                  />
                </div>

                {/* Payment info */}
                <div className="p-4 rounded-xl bg-muted/50 border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">Pagamento</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Il pagamento sarà richiesto dopo la conferma dell'ordine da parte nostra.
                  </p>
                </div>

                {/* Total & Submit */}
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Totale</span>
                    <span>€{totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  className="w-full h-12 text-base rounded-xl gap-2"
                  onClick={handleSubmitOrder}
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Invia ordine
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <BottomNavigation activeTab="prodotti" onMenuClick={() => setMenuOpen(true)} />
        <MenuDrawer open={menuOpen} onOpenChange={setMenuOpen} />
      </div>
    </PageTransition>
  );
};

export default Checkout;
